import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { CommonModule, formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { DeletePolicyDialogComponent } from '../../policies/components/delete-policy-dialog/delete-policy-dialog.component';
import { Logger } from '../../../infrastructure/logging/logger';
import { Country } from '../../../shared/address/Country';
import { CountryService } from '../../../shared/reference/country.service';
import { getStateCode } from '../../../utilities/AddressUtility';
import { getCountryCodeFromCountryId } from '../../../utilities/CountryUtility';
import { BadGuestsColumns, BadGuest } from '../models/BadGuest';
import { BadGuestsService } from '../services/bad-guests.service';

@Component({
  selector: 'app-bad-guests',
  templateUrl: './bad-guests.component.html',
  styleUrls: ['./bad-guests.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatButtonModule,
    MatIcon, 
    MatCard,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatProgressSpinner,
    FormsModule
  ]
})
export class BadGuestsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  loading = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<BadGuest>();
  displayedColumns = BadGuestsColumns;
  tableLength = 0;
  pageSize = 10;
  pageNumber: number = 1;;
  badGuests!: BadGuest[];
  mappedBadGuests: BadGuest[] = [];
  countries!: Country[];
  displayedColumnNames: string[] = [];
  searchKeyword: string = '';

  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private router: Router,
    private badGuestsService: BadGuestsService,
    private countryService: CountryService,
    private dialog: MatDialog,
    private logService: Logger,
    private matSnackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initTableColumns();
    this.loadPage();

    const storedItemsPerPage = localStorage.getItem('badGuestItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    
    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      this.countries = await this.countryService.getCountries();

      this.badGuests = await this.badGuestsService.getBadGuests();
      this.mappedBadGuests = this.badGuests.map(bg => this.buildBadGuestTableRow(bg));
      this.updateTableDataSource(this.mappedBadGuests);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Bad Guests', 'An error occurred while retrieving Bad Guests');
    } finally {
      this.loading.next(false);
    }
  }

  setPagination() {
    this.dataSource = new MatTableDataSource(this.dataSource.data.slice(0, 10));
  }

  buildBadGuestTableRow(badGuest: BadGuest): any {
    return {
      id: badGuest.id,
      first_name: badGuest.first_name,
      middle_name: badGuest.middle_name,
      last_name: badGuest.last_name,
      full_name: badGuest.first_name + ' ' + badGuest.last_name,
      birth_date: badGuest.birth_date != "0000-00-00 00:00:00" ? formatDate(badGuest.birth_date, 'MMM d, y', 'en-US', 'UTC') : null,
      phone: badGuest.phone,
      email: badGuest.email,
      national_number: badGuest.national_number,
      ssn: badGuest.ssn,
      driver_license: badGuest.drivers_license,
      note: badGuest.note,
      address_id: badGuest.address_id,
      address_line_1: badGuest.address.address_line_1, 
      address_line_2: badGuest.address.address_line_2,
      city: badGuest.address.city,
      state: getStateCode(badGuest.address.state),
      postal_code: badGuest.address.postal_code,
      country_id: badGuest.address.country_id,
      country_code: getCountryCodeFromCountryId(this.countries, badGuest.address.country_id)
    }
  }

  updateTableDataSource(badGuests: any[]) {
    this.dataSource.data = badGuests;
    this.tableLength = badGuests.length;
  }

  onAddBadGuestClick() {
    this.router.navigate(['/bad-guests/form']);
  }

  onEditBadGuestClick(row: any) {
    this.router.navigate(['/bad-guests/form'], { state: { row } });
  }

  onDeleteBadGuestClick(row: any) {
    this.openConfirmDeleteBadGuestDialog(row);
  }

  openConfirmDeleteBadGuestDialog(badGuest: BadGuest) {
    const deleteDialogRef = this.dialog.open(DeletePolicyDialogComponent, {
      width: '55%',
      maxHeight: '300px',
      disableClose: true,
      data: {
        component: 'Bad Guest',
        propertyPolicyExists: false
      }
    });
    deleteDialogRef.afterClosed().subscribe((confirmDelete: boolean) => {
      if (confirmDelete) {
        this.badGuestsService.deleteBadGuest(badGuest.id).then(_ => {
          this.loadPage();
        });
      }
    });
  }

  onSearchEvent(keyword: string): void {
    if (keyword.length === 0) {
      this.updateTableDataSource(this.mappedBadGuests);
      this.setPagination();
    } else {
      this.dataSource.data = this.mappedBadGuests;
      this.dataSource.filter = keyword.trim().toLowerCase();
      this.tableLength = this.dataSource.filteredData.length;
    }
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('badGuestItemsPerPage', event.pageSize.toString());
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.dataSource = new MatTableDataSource(this.mappedBadGuests);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate(['/bad-guests/form'], { state: { row } });
  }
}